/**
 * Demo-mode dataset: the reception desktop's sample data
 * (`concierge-desktop/screens/all-visits.md`, Sample data, and the Paper
 * board "All visits · E · All 26 columns"), so the demo build and every
 * design version show the same people, times and states. Customer Oceanic
 * Metals; buildings Birmingham Rum Runner Works, Speechmark and Hopper House.
 *
 * Shapes are the exact wire shapes (`api-types.ts`); every service
 * short-circuits to these rows when PLATFORM_MODE is off. Dates are relative
 * to "today", so the grids always have a live-looking day: today's nine,
 * tomorrow's three, an empty day after, and two visitors four days out.
 * `DEMO_RECEPTION` holds what reception reads that the visit grid does not
 * serve yet (see `visits/visit-row.ts`).
 */

import type {
  ApiAuditEntry,
  ApiQuestionnaire,
  ApiVisit,
  ApiVisitor,
  ApiVisitorTypeSettings,
} from '../services/api-types';
import type { LocationOption } from '../services/locations.service';
import type { VisitQuestionnaire } from '../services/questionnaire-api.service';
import type { ReceptionExtras } from '../visits/visit-row';

/** Local date-time ISO for today+`dayOffset` at `hh:mm`. */
function at(dayOffset: number, hh: number, mm = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

/**
 * The demo operator's division: its logo is the mark, top left (app-frame.md,
 * Data), dark artwork on light papers and light artwork on dark ones. A
 * made-up wordmark for the made-up customer.
 */
export const DEMO_DIVISION = {
  name: 'Oceanic Metals',
  logo: null,
  logoLight: 'demo/brand/oceanic-metals-light.svg',
  logoDark: 'demo/brand/oceanic-metals-dark.svg',
  primaryColor: '#1f5f8b',
} as const;

/** The demo customer: the fallback when the operator has no division. */
export const DEMO_COMPANY = {
  name: 'Oceanic Metals',
  logo: null,
  logoLight: null,
  logoDark: null,
  primaryColor: '#1f5f8b',
} as const;

/**
 * The sites, from the Settings app: three Birmingham buildings, two in the
 * UK South, two in Spain and two outside a campus. Visits use the first,
 * second and fourth.
 */
export const DEMO_LOCATIONS: LocationOption[] = [
  { _id: 'loc-rrw', name: 'Birmingham Rum Runner Works' },
  { _id: 'loc-hop', name: 'Hopper House' },
  { _id: 'loc-hag', name: 'Birmingham (Hagley Road)' },
  { _id: 'loc-spm', name: 'Speechmark' },
  { _id: 'loc-vtn', name: 'VodafoneThree Newbury' },
  { _id: 'loc-bcn', name: 'Barcelona – Sant Cugat' },
  { _id: 'loc-lco', name: 'La Coruña' },
  { _id: 'loc-ams', name: 'Amsterdam' },
  { _id: 'loc-brg', name: 'Bergen' },
];

/** Louis's type names: one set of names across the product. */
export const DEMO_VISITOR_TYPES = [
  { _id: 'vt-guest', name: 'Standard guest' },
  { _id: 'vt-contractor', name: 'Contractor' },
  { _id: 'vt-supplier', name: 'Supplier' },
  { _id: 'vt-interview', name: 'Interview candidate' },
  { _id: 'vt-starter', name: 'New starter' },
  { _id: 'vt-vip', name: 'VIP' },
];

export const DEMO_TYPE_SETTINGS: ApiVisitorTypeSettings[] = [
  {
    _id: 'vts-guest',
    visitorTypeId: 'vt-guest',
    locationId: 'loc-rrw',
    name: 'Standard guest',
    enabled: true,
    isDefault: true,
    visitorFields: {
      email: { enabled: true, required: false },
      mobileNumber: { enabled: true, required: false },
      company: { enabled: true, required: false },
      jobTitle: { enabled: false, required: false },
      carReg: { enabled: true, required: false },
      visiting: { enabled: true, required: true },
    },
    requiresVerification: false,
    registrationNotifyVisitor: true,
    registrationNotifyingTiming: 'Immediate',
  },
  {
    _id: 'vts-contractor',
    visitorTypeId: 'vt-contractor',
    locationId: 'loc-rrw',
    name: 'Contractor',
    enabled: true,
    isDefault: false,
    visitorFields: {
      email: { enabled: true, required: true },
      mobileNumber: { enabled: true, required: true },
      company: { enabled: true, required: true },
      jobTitle: { enabled: true, required: false },
      carReg: { enabled: true, required: false },
      visiting: { enabled: true, required: true },
    },
    requiresVerification: true,
    registrationNotifyVisitor: true,
    registrationNotifyingTiming: 'DaysBefore',
    daysBeforeInvitingVisitorNotified: 2,
  },
  {
    _id: 'vts-supplier',
    visitorTypeId: 'vt-supplier',
    locationId: 'loc-rrw',
    name: 'Supplier',
    enabled: true,
    isDefault: false,
    visitorFields: {
      email: { enabled: true, required: false },
      mobileNumber: { enabled: false, required: false },
      company: { enabled: true, required: true },
      jobTitle: { enabled: false, required: false },
      carReg: { enabled: true, required: false },
      visiting: { enabled: true, required: true },
    },
    requiresVerification: false,
    registrationNotifyVisitor: false,
  },
];

export const DEMO_QUESTIONNAIRE: ApiQuestionnaire = {
  _id: 'q-contractor',
  enabled: true,
  questions: [
    {
      label: 'Have you completed the site induction?',
      fields: [{ id: 'qf-1', type: 'SingleChoice', options: ['Yes', 'No'] }],
    },
    {
      label: 'Which areas will you be working in?',
      fields: [
        {
          id: 'qf-2',
          type: 'MultipleChoice',
          options: ['Plant room', 'Roof', 'Office floors', 'Loading bay'],
        },
      ],
    },
    {
      label: 'Name of your site supervisor',
      fields: [{ id: 'qf-3', type: 'ShortAnswer' }],
    },
  ],
};

/**
 * The people. The twelve of the address book's sample data
 * (concierge-desktop screens/address-book.md; the table in Rahim's 22 Oct
 * brief) carry its contact, care and emergency details, tags, private flag,
 * last visit and visit count; Marta Ruiz and Tom Hale are known but not
 * booked. The rest (Owen, Tomasz, Sofia, Ibrahim) are All visits' cast; the
 * demo store works out their last visit and count from their visits.
 */
export const DEMO_VISITORS: ApiVisitor[] = [
  person('p-grace', 'Grace', 'Okafor', {
    email: 'grace@freshfork.co.uk',
    mobile: '7700 900118',
    company: 'Fresh Fork Catering',
    jobTitle: 'Delivery lead',
    carRegistration: 'FF19 KTN',
    accessNeeds: 'None',
    dietaryRequirements: 'None',
    tags: ['Supplier'],
    lastVisit: 0,
    visitCount: 38,
    pin: '7330',
    type: ['vt-supplier', 'Supplier'],
    since: -400,
  }),
  person('p-priya', 'Priya', 'Nadar', {
    email: 'priya.nadar@meridianfm.co.uk',
    mobile: '7700 900629',
    company: 'Meridian Facilities',
    jobTitle: 'Site supervisor',
    carRegistration: 'MF21 KXP',
    accessNeeds: 'Visual support',
    emergency: ['Ravi Nadar', '+44 7700 900630'],
    tags: ['Contractor'],
    lastVisit: 0,
    visitCount: 6,
    type: ['vt-contractor', 'Contractor'],
    since: -120,
  }),
  person('p-henrik', 'Henrik', 'Larsson', {
    email: 'henrik.larsson@storaenso.com',
    countryCode: '+46',
    mobile: '70 123 45 67',
    company: 'Stora Enso',
    jobTitle: 'Head of sourcing',
    accessNeeds: 'Reduced mobility',
    dietaryRequirements: 'Gluten-free, Dairy-free',
    emergency: ['Anna Larsson', '+46 70 987 65 43'],
    tags: ['Partner'],
    lastVisit: 0,
    visitCount: 9,
    pin: '5521',
    type: ['vt-guest', 'Standard guest'],
    since: -60,
  }),
  person('p-owen', 'Owen', 'Reilly', {
    email: 'owen.reilly@speechmark.io',
    mobile: '07506 375838',
    company: 'Speechmark Media',
    jobTitle: 'Product Designer',
    carRegistration: 'R18 HXM',
    pin: '2222',
    type: ['vt-guest', 'Standard guest'],
    since: -300,
  }),
  person('p-tomasz', 'Tomasz', 'Nowak', {
    email: 't.nowak@oceanicmetals.com',
    mobile: '07533 990021',
    company: 'Oceanic Metals',
    jobTitle: 'Data Analyst',
    pin: '8276',
    type: ['vt-starter', 'New starter'],
    since: -14,
  }),
  person('p-aisha', 'Aisha', 'Khan', {
    email: 'aisha.khan@northgatelegal.co.uk',
    mobile: '7700 900481',
    company: 'Northgate Legal',
    jobTitle: 'Senior associate',
    accessNeeds: 'None',
    dietaryRequirements: 'Vegetarian',
    tags: ['Legal'],
    lastVisit: 0,
    visitCount: 4,
    pin: '6144',
    type: ['vt-guest', 'Standard guest'],
    since: -210,
  }),
  person('p-lucy', 'Lucy', 'Bennett', {
    email: 'lucy@brightline.studio',
    mobile: '7700 900554',
    company: 'Brightline Studio',
    jobTitle: 'Designer',
    dietaryRequirements: 'Vegan',
    lastVisit: 6,
    visitCount: 1,
    pin: '1903',
    type: ['vt-guest', 'Standard guest'],
    since: -90,
  }),
  person('p-sofia', 'Sofia', 'Marchetti', {
    email: 'sofia.marchetti@gmail.com',
    mobile: '07700 900412',
    jobTitle: 'Senior Product Designer',
    pin: '4819',
    type: ['vt-interview', 'Interview candidate'],
    since: -7,
  }),
  person('p-james', 'James', 'Whitfield', {
    email: 'j.whitfield@vodafonethree.com',
    mobile: '7700 900007',
    company: 'VodafoneThree',
    jobTitle: 'Chief operating officer',
    carRegistration: 'VT23 COO',
    accessNeeds: 'None',
    dietaryRequirements: 'Pescatarian',
    isPrivate: true,
    lastVisit: 22,
    visitCount: 3,
    pin: '9012',
    vip: true,
    type: ['vt-vip', 'VIP'],
    since: -500,
  }),
  person('p-daniel', 'Daniel', 'Osei', {
    email: 'd.osei@liftcare.co.uk',
    mobile: '7700 900362',
    company: 'LiftCare UK',
    jobTitle: 'Lift engineer',
    carRegistration: 'LC70 UKE',
    emergency: ['Grace Osei', '+44 7700 900363'],
    tags: ['Contractor'],
    lastVisit: 14,
    visitCount: 7,
    type: ['vt-contractor', 'Contractor'],
    since: -45,
  }),
  person('p-elena', 'Elena', 'Volkova', {
    email: 'elena@santcugat.es',
    countryCode: '+34',
    mobile: '612 345 678',
    company: 'Sant Cugat Partners',
    jobTitle: 'Partner',
    accessNeeds: 'Hearing support',
    dietaryRequirements: 'None',
    tags: ['Partner'],
    lastVisit: 63,
    visitCount: 2,
    pin: '2468',
    type: ['vt-guest', 'Standard guest'],
    since: -180,
  }),
  person('p-chidi', 'Chidi', 'Balogun', {
    email: 'chidi@printworks.co.uk',
    mobile: '7700 900215',
    company: 'PrintWorks Supplies',
    jobTitle: 'Account manager',
    tags: ['Supplier'],
    lastVisit: 37,
    visitCount: 12,
    type: ['vt-supplier', 'Supplier'],
    since: -260,
  }),
  person('p-sarah', 'Sarah', 'Lindgren', {
    email: 'sarah.lindgren@vodafonethree.com',
    mobile: '7700 900832',
    company: 'VodafoneThree',
    jobTitle: 'Procurement director',
    dietaryRequirements: 'Kosher',
    lastVisit: 28,
    visitCount: 5,
    vip: true,
    type: ['vt-vip', 'VIP'],
    since: -30,
  }),
  person('p-ibrahim', 'Ibrahim', 'Farah', {
    email: 'i.farah@gencat.cat',
    company: 'Catalonian Government',
    jobTitle: 'Trade Adviser',
    type: ['vt-guest', 'Standard guest'],
    since: -3,
  }),
  // Known, not booked (address-book.md, Sample data).
  person('p-marta', 'Marta', 'Ruiz', {
    email: 'm.ruiz@gencat.cat',
    countryCode: '+34',
    mobile: '655 901 223',
    company: 'Catalonian Government',
    jobTitle: 'Trade adviser',
    accessNeeds: 'Wheelchair access, Assistance dog',
    dietaryRequirements: 'Halal',
    emergency: ['Jordi Ruiz', '+34 655 901 224'],
    tags: ['Government'],
    lastVisit: 137,
    visitCount: 1,
    type: ['vt-guest', 'Standard guest'],
    since: -200,
  }),
  person('p-tom', 'Tom', 'Hale', {
    email: 'tomhale@gmail.com',
    lastVisit: null,
    visitCount: 0,
    type: ['vt-guest', 'Standard guest'],
    since: -20,
  }),
];

interface PersonFields {
  email?: string;
  /** Defaults to +44 for a number without its own code. */
  countryCode?: string;
  mobile?: string;
  company?: string;
  jobTitle?: string;
  carRegistration?: string;
  accessNeeds?: string;
  dietaryRequirements?: string;
  emergency?: [name: string, phone: string];
  tags?: string[];
  isPrivate?: boolean;
  /**
   * Days ago the person last visited (null: never). Unset: the demo store
   * works it out, and the count, from their visits.
   */
  lastVisit?: number | null;
  visitCount?: number;
  pin?: string;
  vip?: boolean;
  type: [id: string, name: string];
  /** Days ago the person joined the address book. */
  since: number;
}

/** Local YYYY-MM-DD for `days` before today. */
function dayAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function person(_id: string, firstName: string, lastName: string, f: PersonFields): ApiVisitor {
  return {
    _id,
    firstName,
    lastName,
    email: f.email ?? null,
    mobile: f.mobile ?? null,
    countryCode: f.countryCode ?? (f.mobile && !f.mobile.startsWith('+') ? '+44' : null),
    company: f.company ?? null,
    jobTitle: f.jobTitle ?? null,
    carRegistration: f.carRegistration ?? null,
    accessNeeds: f.accessNeeds ?? null,
    dietaryRequirements: f.dietaryRequirements ?? null,
    emergencyContactName: f.emergency?.[0] ?? null,
    emergencyContactPhone: f.emergency?.[1] ?? null,
    tags: f.tags ?? [],
    vip: f.vip ?? false,
    isPrivate: f.isPrivate ?? false,
    isInAddressBook: true,
    pin: f.pin ?? null,
    createdAt: at(f.since, 9),
    visitorTypeId: f.type[0],
    visitorType: f.type[1],
    ...(f.lastVisit !== undefined
      ? {
          lastVisitOn: f.lastVisit === null ? null : dayAgo(f.lastVisit),
          visitCount: f.visitCount ?? 0,
        }
      : {}),
  };
}

/** Hosts (people and team inboxes) and the organisers who booked for them. */
const HOSTS = {
  facilities: {
    userId: 'h-facilities',
    name: 'Facilities Team',
    email: 'facilities@oceanicmetals.com',
  },
  marcus: { userId: 'h-marcus', name: 'Marcus Webb', email: 'marcus.webb@oceanicmetals.com' },
  dianne: { userId: 'h-dianne', name: 'Dianne Russell', email: 'dianne.russell@oceanicmetals.com' },
  people: { userId: 'h-people', name: 'People Team', email: 'people@oceanicmetals.com' },
  dean: { userId: 'h-dean', name: 'Dean Bagnall', email: 'dean.bagnall@oceanicmetals.com' },
  nathan: { userId: 'h-nathan', name: 'Nathan Thomas', email: 'nathan.thomas@oceanicmetals.com' },
};

const ORGANISERS = {
  nadia: { userId: 'h-nadia', name: 'Nadia Hassan', email: 'nadia.hassan@oceanicmetals.com' },
  marcus: HOSTS.marcus,
  dianne: HOSTS.dianne,
  ruth: { userId: 'h-ruth', name: 'Ruth Coker', email: 'ruth.coker@oceanicmetals.com' },
  ellen: { userId: 'h-ellen', name: 'Ellen Pierce', email: 'ellen.pierce@oceanicmetals.com' },
};

export const DEMO_HOSTS = [
  ...Object.values(HOSTS),
  ORGANISERS.nadia,
  ORGANISERS.ruth,
  ORGANISERS.ellen,
];

function visitorRef(id: string): ApiVisitor {
  const v = DEMO_VISITORS.find((x) => x._id === id);
  if (!v) throw new Error(`demo visitor ${id} missing`);
  return v;
}

const SITES = {
  rrw: { _id: 'loc-rrw', name: 'Birmingham Rum Runner Works' },
  spm: { _id: 'loc-spm', name: 'Speechmark' },
  hop: { _id: 'loc-hop', name: 'Hopper House' },
};

type Host = (typeof HOSTS)[keyof typeof HOSTS];
type Organiser = (typeof ORGANISERS)[keyof typeof ORGANISERS];

interface VisitFields {
  visitor: string;
  site: keyof typeof SITES;
  day: number;
  arrive: [number, number];
  leave: [number, number];
  host: Host;
  organiser: Organiser;
  status?: 'expected' | 'signedIn' | 'signedOut';
  signedIn?: [number, number];
  signedOut?: [number, number];
  badge?: string;
  notes?: string;
  tags?: string[];
  verified?: boolean;
}

function visit(_id: string, f: VisitFields): ApiVisit {
  const who = visitorRef(f.visitor);
  const signInTime = f.signedIn ? at(f.day, ...f.signedIn) : null;
  const signOutTime = f.signedOut ? at(f.day, ...f.signedOut) : null;
  const status = f.status ?? 'expected';
  return {
    _id,
    locationId: SITES[f.site]._id,
    visitorId: who._id,
    expectedDate: at(f.day, ...f.arrive),
    expectedEnd: at(f.day, ...f.leave),
    visiting: f.host,
    hosts: [
      { ...f.host, role: 'host' },
      ...(f.organiser.userId === f.host.userId ? [] : [{ ...f.organiser, role: 'organiser' }]),
    ],
    signInStatus: status,
    checkInStatus:
      status === 'signedIn' ? 'checkedIn' : status === 'signedOut' ? 'checkedOut' : null,
    questionnaireStatus: null,
    badgeNumber: f.badge ?? null,
    notes: f.notes ?? null,
    tags: f.tags ?? [],
    signInTime,
    signOutTime,
    signInLog: [
      ...(signInTime ? [{ timestamp: signInTime, status: 'signedIn', method: 'manual' }] : []),
      ...(signOutTime ? [{ timestamp: signOutTime, status: 'signedOut', method: 'manual' }] : []),
    ],
    checkInLog: [
      ...(signInTime ? [{ timestamp: signInTime, status: 'checkedIn', method: 'admin' }] : []),
      ...(signOutTime ? [{ timestamp: signOutTime, status: 'checkedOut', method: 'admin' }] : []),
    ],
    verification: { verified: f.verified ?? true },
    sendInvitation: true,
    visitor: who,
    location: SITES[f.site],
    visitorTypeId: who.visitorTypeId,
    visitorType: who.visitorType,
  };
}

export const DEMO_VISITS: ApiVisit[] = [
  // ── Today: nine visitors (the Renesas site tour is paused with group rows) ──
  visit('v-grace', {
    visitor: 'p-grace',
    site: 'spm',
    day: 0,
    arrive: [7, 30],
    leave: [10, 0],
    host: HOSTS.facilities,
    organiser: ORGANISERS.nadia,
    status: 'signedIn',
    signedIn: [7, 41],
    badge: '1030302355',
    notes: 'All-hands lunch drop — kitchen entrance.',
    tags: ['Deliveries'],
  }),
  visit('v-priya', {
    visitor: 'p-priya',
    site: 'rrw',
    day: 0,
    arrive: [8, 30],
    leave: [13, 0],
    host: HOSTS.marcus,
    organiser: ORGANISERS.marcus,
    verified: false,
    notes: 'HVAC contractor — roof access, must sign in at the loading bay.',
    tags: ['Escort required', 'Hi-vis'],
  }),
  visit('v-henrik', {
    visitor: 'p-henrik',
    site: 'spm',
    day: 0,
    arrive: [8, 30],
    leave: [16, 30],
    host: HOSTS.dianne,
    organiser: ORGANISERS.dianne,
    status: 'signedIn',
    signedIn: [8, 52],
    badge: '1030302361',
    notes: 'Three-day integration workshop; locker 12 assigned for the week.',
    tags: ['Multi-day'],
  }),
  visit('v-owen', {
    visitor: 'p-owen',
    site: 'spm',
    day: 0,
    arrive: [8, 45],
    leave: [17, 0],
    host: HOSTS.dianne,
    organiser: ORGANISERS.dianne,
    status: 'signedIn',
    signedIn: [9, 12],
    badge: '1030302341',
    notes: 'Prefers the north entrance; parking pre-booked.',
    tags: ['Regular'],
  }),
  visit('v-tomasz', {
    visitor: 'p-tomasz',
    site: 'spm',
    day: 0,
    arrive: [9, 0],
    leave: [17, 30],
    host: HOSTS.people,
    organiser: ORGANISERS.nadia,
    notes: 'Day-one induction; IT to issue laptop at 10:00.',
    tags: ['Onboarding'],
  }),
  visit('v-aisha', {
    visitor: 'p-aisha',
    site: 'rrw',
    day: 0,
    arrive: [9, 15],
    leave: [12, 30],
    host: HOSTS.marcus,
    organiser: ORGANISERS.marcus,
    status: 'signedOut',
    signedIn: [9, 21],
    signedOut: [12, 34],
    badge: '1030302348',
    notes: 'Contract signing — left before lunch.',
    tags: ['Legal'],
  }),
  visit('v-lucy', {
    visitor: 'p-lucy',
    site: 'hop',
    day: 0,
    arrive: [9, 40],
    leave: [12, 0],
    host: HOSTS.dean,
    organiser: ORGANISERS.ruth,
    status: 'signedIn',
    signedIn: [9, 48],
    badge: '1030302370',
    notes: 'Workshop co-facilitator.',
    tags: ['Design partner'],
  }),
  visit('v-sofia', {
    visitor: 'p-sofia',
    site: 'hop',
    day: 0,
    arrive: [10, 0],
    leave: [11, 30],
    host: HOSTS.dean,
    organiser: ORGANISERS.ruth,
    notes: 'Interview — Senior Product Designer. Portfolio review in Room 4.',
    tags: ['Candidate'],
  }),
  visit('v-james', {
    visitor: 'p-james',
    site: 'rrw',
    day: 0,
    arrive: [11, 0],
    leave: [15, 0],
    host: HOSTS.nathan,
    organiser: ORGANISERS.ellen,
    notes: 'Board sponsor visit — greet at reception, escort to the 4th floor boardroom.',
    tags: ['Executive'],
  }),
  // ── Tomorrow: three ──
  visit('v-daniel', {
    visitor: 'p-daniel',
    site: 'rrw',
    day: 1,
    arrive: [8, 0],
    leave: [12, 0],
    host: HOSTS.facilities,
    organiser: ORGANISERS.marcus,
    verified: false,
    notes: 'Scheduled lift maintenance — permit to work required.',
    tags: ['Escort required'],
  }),
  visit('v-elena', {
    visitor: 'p-elena',
    site: 'spm',
    day: 1,
    arrive: [10, 30],
    leave: [16, 0],
    host: HOSTS.nathan,
    organiser: ORGANISERS.ellen,
    notes: 'Quarterly account review.',
    tags: ['Partner'],
  }),
  visit('v-chidi', {
    visitor: 'p-chidi',
    site: 'hop',
    day: 1,
    arrive: [14, 0],
    leave: [15, 0],
    host: HOSTS.facilities,
    organiser: ORGANISERS.nadia,
    notes: 'Toner + consumables restock.',
    tags: ['Deliveries'],
  }),
  // ── The day after: no visitors scheduled. Four days out: two ──
  visit('v-sarah', {
    visitor: 'p-sarah',
    site: 'rrw',
    day: 4,
    arrive: [9, 30],
    leave: [12, 0],
    host: HOSTS.nathan,
    organiser: ORGANISERS.ellen,
    notes: 'Site walk with the board.',
    tags: ['Executive'],
  }),
  visit('v-ibrahim', {
    visitor: 'p-ibrahim',
    site: 'spm',
    day: 4,
    arrive: [11, 0],
    leave: [13, 0],
    host: HOSTS.dianne,
    organiser: ORGANISERS.dianne,
    notes: 'Trade mission follow-up.',
  }),
  // ── Earlier visits, for the panel's History tab ──
  visit('v-henrik-day1', {
    visitor: 'p-henrik',
    site: 'spm',
    day: -1,
    arrive: [8, 30],
    leave: [16, 30],
    host: HOSTS.dianne,
    organiser: ORGANISERS.dianne,
    status: 'signedOut',
    signedIn: [8, 41],
    signedOut: [16, 48],
    badge: '1030302361',
    notes: 'Three-day integration workshop; locker 12 assigned for the week.',
    tags: ['Multi-day'],
  }),
  visit('v-priya-past', {
    visitor: 'p-priya',
    site: 'rrw',
    day: -21,
    arrive: [8, 0],
    leave: [12, 0],
    host: HOSTS.marcus,
    organiser: ORGANISERS.marcus,
    status: 'signedOut',
    signedIn: [8, 6],
    signedOut: [11, 52],
    badge: '1030302210',
    notes: 'Chiller inspection.',
    tags: ['Hi-vis'],
  }),
  visit('v-aisha-past', {
    visitor: 'p-aisha',
    site: 'rrw',
    day: -35,
    arrive: [14, 0],
    leave: [15, 30],
    host: HOSTS.marcus,
    organiser: ORGANISERS.marcus,
    status: 'signedOut',
    signedIn: [13, 58],
    signedOut: [15, 41],
    badge: '1030302187',
    tags: ['Legal'],
  }),
];

/**
 * What reception reads on each demo visit that the visit grid does not serve
 * (requirements, the invitation, badge printing, bookings) or that
 * the app does not select yet. Keyed by visit id; demo mode only.
 */
export const DEMO_RECEPTION: Readonly<Record<string, ReceptionExtras>> = {
  'v-grace': {
    verification: { kind: 'clear' },
    requirements: [
      met('Photo ID', 'Checked today 07:40'),
      met('Hygiene certificate', 'Valid to Mar 2027'),
      met('Questionnaire', 'Not required'),
    ],
    invitation: 'delivered',
    invite: { sentAt: at(-3, 14, 2), sentBy: 'Nadia Hassan', deliveredAt: at(-3, 14, 3) },
    questionnaire: 'notRequired',
    badgePrint: 'printed',
    badgeJob: { printer: 'Speechmark reception', printerReady: true, at: at(0, 7, 42) },
    bookings: ['parking'],
  },
  'v-priya': {
    verification: {
      kind: 'blocking',
      requirements: ['Site safety induction', 'Public liability insurance'],
      held: ['Photo ID', 'Health and safety questionnaire'],
    },
    requirements: [
      {
        name: 'Site safety induction',
        mark: 'missing',
        detail: 'Expired 11 Jan · redo at the kiosk',
        blocking: true,
      },
      {
        name: 'Public liability insurance',
        mark: 'checking',
        detail: 'Uploaded yesterday · waiting for review',
        blocking: true,
      },
      met('Photo ID', 'Seen yesterday'),
      met('Health and safety questionnaire', 'Answered yesterday'),
      {
        name: 'Hot works permit',
        mark: 'optional',
        detail: 'Not seen · optional',
        blocking: false,
      },
    ],
    invitation: 'sent',
    invite: { sentAt: at(-2, 9, 30), sentBy: 'Marcus Webb' },
    questionnaire: 'sent',
    questionnaireDetail: { title: 'Site safety', questions: 6, sentAt: at(-2, 9, 30) },
    accessNeeds: ['Hearing loop'],
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Rum Runner Works reception', printerReady: true },
    bookings: ['desk', 'parking'],
    pin: null,
  },
  'v-henrik': {
    day: { index: 2, of: 3 },
    verification: { kind: 'clear' },
    requirements: [
      met('Photo ID', 'Checked yesterday'),
      met('Questionnaire', 'Completed 3 days ago'),
    ],
    invitation: 'delivered',
    invite: {
      sentAt: at(-5, 11, 0),
      sentBy: 'Dianne Russell',
      deliveredAt: at(-5, 11, 1),
      openedAt: at(-4, 8, 11),
    },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Site safety', questions: 6, answeredAt: at(-3, 8, 20) },
    badgePrint: 'printed',
    badgeJob: { printer: 'Speechmark reception', printerReady: true, at: at(0, 8, 53) },
    bookings: ['desk'],
  },
  'v-owen': {
    verification: { kind: 'override' },
    requirements: [
      { name: 'Site safety induction', mark: 'missing', detail: 'Expired 2 Jun', blocking: true },
      met('Photo ID', 'Checked today 08:58'),
    ],
    override: {
      by: 'Sam Patel',
      at: at(0, 9, 1),
      reason: 'Escorted by host all day, no site access.',
    },
    invitation: 'sent',
    invite: { sentAt: at(-1, 16, 40), sentBy: 'Dianne Russell' },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Site safety', questions: 6, answeredAt: at(-1, 18, 5) },
    badgePrint: 'printed',
    badgeJob: { printer: 'Speechmark reception', printerReady: true, at: at(0, 9, 13) },
    bookings: ['parking'],
  },
  'v-tomasz': {
    verification: { kind: 'warning', requirements: ['NDA'] },
    requirements: [
      { name: 'NDA', mark: 'outstanding', detail: 'Not signed', blocking: false },
      met('Photo ID', 'Checked today 08:44'),
      met('Questionnaire', 'Completed yesterday'),
    ],
    invitation: 'delivered',
    invite: { sentAt: at(-6, 10, 0), sentBy: 'Nadia Hassan', deliveredAt: at(-6, 10, 1) },
    questionnaire: 'notRequired',
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Speechmark reception', printerReady: true },
    bookings: ['desk'],
  },
  'v-aisha': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked today 09:20')],
    invitation: 'sent',
    invite: { sentAt: at(-4, 13, 15), sentBy: 'Marcus Webb' },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Visitor declaration', questions: 3, answeredAt: at(-1, 19, 2) },
    badgePrint: 'printed',
    badgeJob: { printer: 'Rum Runner Works reception', printerReady: true, at: at(0, 9, 22) },
    bookings: [],
  },
  'v-lucy': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked today 09:47')],
    invitation: 'delivered',
    invite: {
      sentAt: at(-3, 14, 2),
      sentBy: 'Ruth Coker',
      deliveredAt: at(-3, 14, 3),
      openedAt: at(-2, 8, 11),
    },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Site safety', questions: 6, answeredAt: at(-2, 8, 20) },
    badgePrint: 'printed',
    badgeJob: { printer: 'Hopper House reception', printerReady: true, at: at(0, 9, 49) },
    bookings: ['room'],
  },
  'v-sofia': {
    verification: {
      kind: 'blocking',
      requirements: ['Photo ID'],
      held: ['Questionnaire'],
    },
    requirements: [
      { name: 'Photo ID', mark: 'checking', detail: 'Uploaded today 08:02', blocking: true },
      met('Questionnaire', 'Completed today 09:40'),
    ],
    invitation: 'sent',
    invite: { sentAt: at(-1, 12, 0), sentBy: 'Ruth Coker' },
    questionnaire: 'complete',
    questionnaireDetail: {
      title: 'Site safety',
      questions: 6,
      answeredAt: at(0, 9, 40),
      answeredBy: 'Sam Patel',
    },
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Hopper House reception', printerReady: false },
    bookings: ['room'],
  },
  'v-james': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked 3 days ago')],
    invitation: 'delivered',
    invite: { sentAt: at(-7, 9, 0), sentBy: 'Ellen Pierce', deliveredAt: at(-7, 9, 1) },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Site safety', questions: 6, answeredAt: at(-5, 17, 30) },
    badgePrint: 'queued',
    badgeJob: { printer: 'Rum Runner Works reception', printerReady: true, at: at(0, 9, 51) },
    bookings: ['parking', 'room'],
  },
  'v-daniel': {
    verification: {
      kind: 'blocking',
      requirements: ['Permit to work'],
      held: ['Site induction'],
    },
    requirements: [
      { name: 'Permit to work', mark: 'missing', detail: 'Not uploaded', blocking: true },
      met('Site induction', 'Checked last week'),
    ],
    invitation: 'scheduled',
    invite: { scheduledFor: at(0, 17, 0) },
    questionnaire: 'scheduled',
    questionnaireDetail: { title: 'Site safety', questions: 6 },
    accessNeeds: ['PEEP on file'],
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Rum Runner Works reception', printerReady: true },
    bookings: ['parking'],
    pin: null,
  },
  'v-elena': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked last month')],
    invitation: 'delivered',
    invite: { sentAt: at(-2, 15, 20), sentBy: 'Ellen Pierce', deliveredAt: at(-2, 15, 21) },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Visitor declaration', questions: 3, answeredAt: at(-1, 9, 4) },
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Speechmark reception', printerReady: true },
    bookings: [],
  },
  'v-chidi': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked last month')],
    invitation: 'sent',
    invite: { sentAt: at(-1, 10, 10), sentBy: 'Nadia Hassan' },
    questionnaire: 'sent',
    questionnaireDetail: {
      title: 'Visitor declaration',
      questions: 3,
      sentAt: at(-1, 10, 10),
      lastReminderAt: null,
    },
    badgePrint: 'failed',
    badgeJob: {
      printer: 'Hopper House reception',
      printerReady: true,
      at: at(0, 9, 51),
      reason: 'Out of labels',
    },
    bookings: ['parking'],
    pin: null,
  },
  'v-sarah': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked last year')],
    invitation: 'sent',
    invite: { sentAt: at(-1, 8, 0), sentBy: 'Ellen Pierce' },
    questionnaire: 'complete',
    questionnaireDetail: { title: 'Site safety', questions: 6, answeredAt: at(-1, 12, 0) },
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Rum Runner Works reception', printerReady: true },
    bookings: ['parking', 'room'],
  },
  'v-ibrahim': {
    verification: { kind: 'clear' },
    requirements: [met('Photo ID', 'Checked last year')],
    invitation: 'notSent',
    questionnaire: 'notSent',
    questionnaireDetail: { title: 'Visitor declaration', questions: 3 },
    badgePrint: 'notPrinted',
    badgeJob: { printer: 'Speechmark reception', printerReady: true },
    bookings: [],
  },
  'v-henrik-day1': {
    day: { index: 1, of: 3 },
    verification: { kind: 'clear' },
    invitation: 'sent',
    questionnaire: 'complete',
    badgePrint: 'printed',
    bookings: ['desk', 'locker'],
  },
};

function met(name: string, detail: string) {
  return { name, mark: 'met' as const, detail, blocking: true };
}

/**
 * The employees behind the demo hosts and organisers: what the host card and
 * host details read (the API: `employee(id)`, `hostParty(visitId)`,
 * `bookingsForEmployee`). Team inboxes have no presence or desk.
 */
export interface DemoEmployee {
  readonly jobTitle: string | null;
  readonly department: string | null;
  readonly mobile: string | null;
  readonly manager: string | null;
  readonly presence: 'office' | 'home' | 'elsewhere' | 'unknown';
  readonly desk?: {
    readonly label: string;
    readonly building: string;
    readonly from: string;
    readonly to: string;
  } | null;
}

export const DEMO_EMPLOYEES: Readonly<Record<string, DemoEmployee>> = {
  'h-facilities': {
    jobTitle: null,
    department: 'Facilities',
    mobile: null,
    manager: null,
    presence: 'unknown',
  },
  'h-people': {
    jobTitle: null,
    department: 'People',
    mobile: null,
    manager: null,
    presence: 'unknown',
  },
  'h-marcus': {
    jobTitle: 'Estates Manager',
    department: 'Facilities',
    mobile: '+44 7700 900 318',
    manager: 'Nathan Thomas',
    presence: 'office',
    desk: {
      label: 'Desk 2.07 · Floor 2',
      building: 'Birmingham Rum Runner Works',
      from: '08:00',
      to: '16:30',
    },
  },
  'h-dianne': {
    jobTitle: 'Head of Procurement',
    department: 'Procurement',
    mobile: '+44 7700 900 412',
    manager: 'Nathan Thomas',
    presence: 'office',
    desk: { label: 'Desk 4.12 · Floor 4', building: 'Speechmark', from: '08:30', to: '17:30' },
  },
  'h-dean': {
    jobTitle: 'Design Director',
    department: 'Product',
    mobile: '+44 7700 900 127',
    manager: 'Nathan Thomas',
    presence: 'home',
  },
  'h-nathan': {
    jobTitle: 'Product Director',
    department: 'Product',
    mobile: '+44 7700 900 001',
    manager: null,
    presence: 'elsewhere',
  },
  'h-nadia': {
    jobTitle: 'Office Coordinator',
    department: 'Facilities',
    mobile: '+44 7700 900 664',
    manager: 'Marcus Webb',
    presence: 'office',
    desk: { label: 'Desk 1.03 · Floor 1', building: 'Speechmark', from: '08:00', to: '16:00' },
  },
  'h-ruth': {
    jobTitle: 'Executive Assistant',
    department: 'Product',
    mobile: '+44 7700 900 245',
    manager: 'Dean Bagnall',
    presence: 'office',
    desk: { label: 'Desk 3.21 · Floor 3', building: 'Hopper House', from: '09:00', to: '17:00' },
  },
  'h-ellen': {
    jobTitle: 'Chief of Staff',
    department: 'Office of the CEO',
    mobile: '+44 7700 900 532',
    manager: 'Nathan Thomas',
    presence: 'unknown',
  },
};

/**
 * Each location's tag library (Rahim, 9 Oct: the library lives at the
 * location level). Visits pick from it; a new tag joins it.
 */
export const DEMO_TAG_LIBRARY: Readonly<Record<string, readonly string[]>> = {
  'loc-rrw': ['Escort required', 'Hi-vis', 'Legal', 'Executive', 'Parking', 'Press'],
  'loc-spm': ['Deliveries', 'Multi-day', 'Regular', 'Onboarding', 'Partner', 'VIP', 'Lunch booked'],
  'loc-hop': ['Design partner', 'Candidate', 'Deliveries', 'Interview', 'Escort required'],
};

export const DEMO_AUDIT: Record<string, ApiAuditEntry[]> = {
  'v-priya': [
    {
      _id: 'a-1',
      action: 'created',
      category: 'access',
      detail: 'Visit created for Priya Nadar',
      performedByName: 'Marcus Webb',
      timestamp: at(-9, 15, 12),
    },
    {
      _id: 'a-2',
      action: 'inviteSent',
      category: 'access',
      detail: 'Invite sent to p.nadar@meridianfm.co.uk',
      performedByName: 'Marcus Webb',
      timestamp: at(-2, 9, 30),
    },
  ],
  'v-grace': [
    {
      _id: 'a-3',
      action: 'signedIn',
      category: 'access',
      target: 'signedIn',
      performedByName: 'Ruth Walker',
      timestamp: at(0, 7, 41),
    },
  ],
};

/**
 * The platform questionnaire for a demo visit, in `questionnaireFor`'s shape
 * (both halves), so the visit panel's Questionnaire tab has something to show
 * in demo mode and `/preview/visit-questionnaire`. One of each kind of row:
 * answers by the visitor and by reception, built-in "asked before the visit"
 * rows, questions reception may answer (yes/no, multiple choice, text), and
 * ones that stay the visitor's (a question that counts as a requirement, a
 * signed document, a video).
 */
export const DEMO_VISIT_QUESTIONNAIRE: VisitQuestionnaire = {
  attendanceId: 'att-demo',
  beforeArrival: {
    questionnaire: {
      _id: 'qn-before',
      title: 'Before your visit',
      questions: [
        { _id: 'q-purpose', label: 'What is the purpose of your visit?', type: 'shortText' },
        {
          _id: 'q-equipment',
          label: 'Will you bring any equipment on site?',
          type: 'yesNo',
          required: true,
        },
        {
          _id: 'q-areas',
          label: 'Which areas will you visit?',
          type: 'multiSelect',
          fields: [{ options: ['Office floors', 'Plant room', 'Loading bay', 'Roof'] }],
        },
        {
          _id: 'q-cscs',
          label: 'Do you hold a valid CSCS card?',
          type: 'yesNo',
          required: true,
          satisfiesReqId: 'req-cscs',
        },
        {
          _id: 'q-agreement',
          label: 'Sign the visitor agreement',
          type: 'signature',
          required: true,
        },
      ],
    },
    outstanding: [
      {
        questionId: '0000000000000000000a0003',
        preVisitField: 'emergencyContact',
        question: {
          _id: '0000000000000000000a0003',
          label: 'Who should we contact in an emergency?',
          type: 'smartField',
        },
      },
      { questionId: 'q-equipment' },
      { questionId: 'q-areas' },
      { questionId: 'q-cscs' },
      { questionId: 'q-agreement', document: { title: 'Visitor agreement' } },
    ],
    answered: {
      answers: [
        {
          questionId: '0000000000000000000a0001',
          label: 'Is there anything we should know to make your visit accessible?',
          type: 'smartField',
          value: 'A hearing loop at reception, please.',
          answeredAt: at(-1, 18, 12),
          filledByName: null,
        },
        {
          questionId: '0000000000000000000a0002',
          label: 'Do you have any dietary requirements?',
          type: 'smartField',
          value: 'Vegetarian',
          answeredAt: at(-1, 9, 40),
          filledByName: 'Ruth Walker',
        },
        {
          questionId: 'q-purpose',
          label: 'What is the purpose of your visit?',
          type: 'shortText',
          value: 'HVAC service on the roof plant',
          answeredAt: at(-1, 18, 14),
          filledByName: null,
        },
      ],
    },
  },
  onTheDay: {
    questionnaire: {
      _id: 'qn-day',
      title: 'On arrival',
      questions: [
        { _id: 'q-car', label: 'Did you arrive by car?', type: 'yesNo' },
        { _id: 'q-safety', label: 'Watch the site safety video', type: 'video', required: true },
      ],
    },
    outstanding: [{ questionId: 'q-car' }, { questionId: 'q-safety' }],
    answered: { answers: [] },
  },
};
